/**
 * Hosted embed auth bridge — origin-validated postMessage only (never *).
 * Host mints a scoped embed token via POST /cp/mapper/embed/token; never pass session JWT.
 */

const ALLOWED_MESSAGE_TYPES = new Set(['mapper:config', 'mapper:ready', 'mapper:resize']);

export interface EmbedHostMessage {
  type: string;
  config?: Record<string, unknown>;
  token?: string;
}

export function validateParentOrigin(origin: string, allowlist: string[]): boolean {
  if (!origin) return false;
  if (allowlist.length === 0) return false;
  return allowlist.includes(origin);
}

export function postToEmbed(iframe: HTMLIFrameElement | null, message: EmbedHostMessage, targetOrigin: string) {
  if (!iframe?.contentWindow || !targetOrigin) return;
  iframe.contentWindow.postMessage(message, targetOrigin);
}

export function listenEmbedMessages(
  allowlist: string[],
  handler: (data: EmbedHostMessage, origin: string) => void,
) {
  const onMessage = (event: MessageEvent) => {
    if (!validateParentOrigin(event.origin, allowlist)) return;
    if (!event.data || typeof event.data !== 'object') return;
    const type = String((event.data as EmbedHostMessage).type || '');
    if (!ALLOWED_MESSAGE_TYPES.has(type)) return;
    handler(event.data as EmbedHostMessage, event.origin);
  };
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}

export async function mintEmbedToken(
  parentOrigin: string,
  platformEnv: 'prod' | 'sandbox' = 'sandbox',
): Promise<{ token: string; config: Record<string, unknown> }> {
  const { cpFetch } = await import('@/lib/cpClient');
  const res = await cpFetch('/mapper/embed/token', {
    method: 'POST',
    body: JSON.stringify({ parent_origin: parentOrigin, platform_env: platformEnv }),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    const msg = json.error || json.message || 'Failed to mint embed token';
    throw new Error(msg);
  }
  const json = await res.json();
  return { token: json.token, config: json.config || {} };
}
